import { useState, useEffect } from "react";
import { MessageSquareHeart, Trash2, Loader2, Search, ChevronLeft, ChevronRight, Mail, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import toast from "react-hot-toast";
import { useAdminFeedbacks, useDeleteFeedback } from "@/services/adminFeedbackService";

export default function FeedbackPage() {
    const [filterSearch, setFilterSearch] = useState("");
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    const { data: feedbacks, isLoading } = useAdminFeedbacks({ search: filterSearch || undefined });
    const deleteFeedback = useDeleteFeedback();

    const totalItems = feedbacks?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const paginatedFeedbacks = feedbacks?.slice(startIndex, endIndex) ?? [];

    useEffect(() => {
        setPage(1);
    }, [filterSearch]);

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            {/* Header */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6">
                <div className="flex items-start gap-5">
                    <div className="p-4 rounded-3xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
                        <MessageSquareHeart className="w-8 h-8" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
                            Feedback Peserta
                        </h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
                            Masukan yang dikirimkan peserta melalui tautan feedback pada email sertifikat.
                        </p>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200 dark:border-slate-800 shadow-sm shrink-0">
                    <p className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Total Feedback</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-white">{totalItems}</p>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="p-5 md:p-6 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800">
                    <div className="relative w-full max-w-sm">
                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-slate-400" />
                        </div>
                        <Input
                            type="text"
                            placeholder="Cari nama, email, institusi, atau isi feedback..."
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                            className="pl-10 h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl w-full"
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                                <th className="px-6 py-4 font-semibold w-16 text-center">No</th>
                                <th className="px-6 py-4 font-semibold">Pengirim</th>
                                <th className="px-6 py-4 font-semibold">Feedback</th>
                                <th className="px-6 py-4 font-semibold whitespace-nowrap">Waktu</th>
                                <th className="px-6 py-4 font-semibold text-right w-20">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-500" />
                                        Memuat data feedback...
                                    </td>
                                </tr>
                            ) : totalItems === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                                        Belum ada feedback yang masuk.
                                    </td>
                                </tr>
                            ) : (
                                paginatedFeedbacks.map((fb, idx) => (
                                    <tr key={fb.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors align-top">
                                        <td className="px-6 py-4 text-center text-slate-500">{startIndex + idx + 1}</td>
                                        <td className="px-6 py-4 min-w-[180px]">
                                            <div className="font-bold text-slate-900 dark:text-white">{fb.name || "Anonim"}</div>
                                            {fb.email && (
                                                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                    <Mail className="w-3 h-3" /> {fb.email}
                                                </div>
                                            )}
                                            {fb.institution && (
                                                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                                    <Building2 className="w-3 h-3" /> {fb.institution}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-700 dark:text-slate-300 max-w-xl whitespace-pre-wrap">
                                            {fb.message}
                                        </td>
                                        <td className="px-6 py-4 text-xs text-slate-500 whitespace-nowrap">
                                            {fb.created_at ? new Date(fb.created_at).toLocaleString("id-ID") : "-"}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => setDeleteConfirmId(fb.id)}
                                                className="h-8 w-8 text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-900/30"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalItems > 0 && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <span className="text-slate-500 font-medium text-xs sm:text-sm">
                                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                                <select
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Prev</span>
                                </Button>
                                <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm min-w-[3rem] text-center">
                                    {page} / {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="h-8 px-3 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 font-medium"
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Delete Confirm Modal */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Feedback?</DialogTitle>
                        <DialogDescription>Tindakan ini tidak dapat dibatalkan.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>Batal</Button>
                        <Button
                            variant="destructive"
                            disabled={deleteFeedback.isPending}
                            onClick={async () => {
                                if (!deleteConfirmId) return;
                                try {
                                    await deleteFeedback.mutateAsync(deleteConfirmId);
                                    toast.success("Feedback berhasil dihapus.");
                                    setDeleteConfirmId(null);
                                } catch {
                                    toast.error("Gagal menghapus feedback.");
                                }
                            }}
                        >
                            {deleteFeedback.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
